/**
 * Module dependencies.
 */

import { Button, Display, Image } from 'src/components/core';
import {
  RawHtml,
  color,
  media,
  units,
  useBreakpoint
} from '@untile/react-components';

import { ReactElement } from 'react';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * Images.
 */

const images = {
  desktop: '/static/images/error-page@2x.png',
  mobile: '/static/images/error-page-mobile@2x.png'
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  background-color: ${color('backgrounds.taupe02')};
  min-height: 100vh;
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(11.5)};
  position: relative;
  width: 100vw;

  ${media.min('sm')`
    height: 100vh;
    padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(
    5
  )}) 0 0;
  `}

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(5)}) 0 0;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  height: 100%;
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'image image    image'
    '.     title    .'
    '.     subtitle .'
    '.     button   .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  height: 100%;

  ${media.min('sm')`
    grid-template-areas:
      '. title    . .'
      '. subtitle . image'
      '. button   . image'
      '. .        . image';
    grid-template-columns: ${theme('grid.gutterMobile')}px 7fr 3fr ${theme(
    'grid.gutterMobile'
  )}px;
    grid-template-rows: repeat(3, max-content) 1fr;
  `}

  ${media.min('ms')`
    grid-template-areas:
      '. title    . .'
      '. subtitle . .'
      '. button   . image'
      '. .        . image';
  `}

  ${media.min('md')`
    grid-template-areas:
      '. .        . .'
      '. title    . .'
      '. subtitle . .'
      '. button   . image'
      '. .        . image';
    grid-template-columns: 1fr 3fr 1fr 7fr;
    grid-template-rows: ${units(8)} repeat(3, max-content) 1fr;
  `}

  ${media.min('lg')`
    grid-template-areas:
      '. .        . image'
      '. title    . image'
      '. subtitle . image'
      '. button   . image'
      '. .        . image';
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  overflow: hidden;
  pointer-events: none;
  position: relative;

  ${media.min('sm')`
    grid-column: 2 / -1;
  `}

  ${media.min('lg')`
    grid-column: 3 / -1;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Display)`
  grid-area: title;

  ${media.min('ms')`
    grid-column: 2 / 4;
  `}

  ${media.min('xxl')`
    grid-column: 2;
  `}
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Display).attrs({ size: 'medium' })`
  grid-area: subtitle;
  margin-bottom: ${units(2)};

  ${media.min('ms')`
    grid-column: 2 / 4;
    padding-right: 14px;
  `}

  ${media.min('xxl')`
    grid-column: 2;
    padding-right: 14px;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  grid-area: button;
`;

/**
 * `NotFound` page
 */

const NotFound = (): ReactElement => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const isMobile = useBreakpoint('ms', 'max');

  return (
    <Wrapper>
      <StyledContainer>
        <Grid>
          <RawHtml element={Title}>
            {translate('errors:errorPage.title')}
          </RawHtml>

          <Subtitle>{translate('errors:errorPage.description')}</Subtitle>

          <StyledButton
            colorTheme={'secondary'}
            href={routeResolver('home')}
          >
            {translate('common:actions.goBackHome')}
          </StyledButton>

          <ImageWrapper>
            <Image
              alt={'error-page'}
              fill={!isMobile}
              src={isMobile ? images.mobile : images.desktop}
              style={
                isMobile
                  ? {
                      objectFit: 'scale-down',
                      objectPosition: 'right'
                    }
                  : {
                      objectFit: 'cover',
                      objectPosition: 'top'
                    }
              }
              {...(isMobile && {
                height: 354,
                width: 375
              })}
            />
          </ImageWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `NotFound` page.
 */

export default NotFound;
